import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';
import theme from '../../styles';
import Navbar from '../../components/Navbar';

const TikTokCallback = () => {
  const [code, setCode] = useState('');
  const [state, setState] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = 'TikTok Auth Callback - Digital Porters';
    const params = new URLSearchParams(window.location.search);
    const c = params.get('code');
    const s = params.get('state');
    const e = params.get('error') || params.get('error_description');
    if (c) setCode(c);
    if (s) setState(s);
    if (e) setError(e);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <MainContainer>
      <Navbar
        position='sticky'
        customtextcolor={theme.color.textPrimary}
        searchBarBackgroundColor={theme.color.beige}
      />
      <Content>
        {error ? (
          <ErrorCard>
            <Title>Authentication Failed</Title>
            <Body>TikTok returned an error:</Body>
            <CodeBlock error>{error}</CodeBlock>
            <Body>Close this tab and try the auth flow again.</Body>
          </ErrorCard>
        ) : code ? (
          <SuccessCard>
            <Title>Authentication Successful</Title>
            <Body>
              Copy the authorization code below and paste it back into your
              terminal where the CLI is waiting.
            </Body>
            <Label>Authorization code</Label>
            <CodeBlock>{code}</CodeBlock>
            <CopyButton onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy code'}
            </CopyButton>
            {state && (
              <>
                <Label>State (for verification)</Label>
                <CodeBlock subtle>{state}</CodeBlock>
              </>
            )}
            <FootNote>
              You can close this tab once the code is pasted into the
              terminal.
            </FootNote>
          </SuccessCard>
        ) : (
          <SuccessCard>
            <Title>TikTok Auth Callback</Title>
            <Body>
              No authorization code was found in the URL. This page is the
              redirect destination for the Digital Porters TikTok
              integration. If you reached this page directly, you can close
              the tab.
            </Body>
          </SuccessCard>
        )}
      </Content>
    </MainContainer>
  );
};

const MainContainer = styled('div')(() => ({
  backgroundColor: theme.color.cream,
  minHeight: '100vh',
}));
const Content = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '60px 20px',
}));
const SuccessCard = styled('div')(() => ({
  backgroundColor: theme.color.light,
  padding: 40,
  borderRadius: theme.borderRadius.medium,
  maxWidth: 720,
  width: '100%',
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
}));
const ErrorCard = styled(SuccessCard)(() => ({
  borderTop: `4px solid ${theme.color.brick}`,
}));
const Title = styled('h1')(() => ({
  color: theme.color.primary,
  fontFamily: theme.fontFamily.primary,
  fontSize: 32,
  margin: '0 0 16px 0',
}));
const Body = styled('p')(() => ({
  color: theme.color.textPrimary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 16,
  lineHeight: 1.6,
  margin: '0 0 20px 0',
}));
const Label = styled('div')(() => ({
  color: theme.color.textSecondary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 8,
  marginTop: 12,
}));
const CodeBlock = styled('pre')(({ subtle, error }) => ({
  backgroundColor: error
    ? '#FEE'
    : subtle
      ? theme.color.beige
      : theme.color.cream,
  color: error ? theme.color.brick : theme.color.brown,
  fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, monospace',
  fontSize: 14,
  padding: 16,
  borderRadius: theme.borderRadius.small,
  margin: '0 0 16px 0',
  overflowX: 'auto',
  wordBreak: 'break-all',
  whiteSpace: 'pre-wrap',
  border: `1px solid ${theme.color.beige}`,
}));
const CopyButton = styled('button')(() => ({
  backgroundColor: theme.color.primary,
  color: theme.color.light,
  fontFamily: theme.fontFamily.secondary,
  fontSize: 14,
  fontWeight: 600,
  padding: '12px 24px',
  border: 'none',
  borderRadius: theme.borderRadius.small,
  cursor: 'pointer',
  marginBottom: 24,
  '&:hover': {
    opacity: 0.9,
  },
}));
const FootNote = styled('p')(() => ({
  color: theme.color.textSecondary,
  fontFamily: theme.fontFamily.tertiary,
  fontSize: 13,
  fontStyle: 'italic',
  margin: 0,
}));

export default TikTokCallback;
